import React, { useState, useEffect, useContext } from 'react';
import { Event } from '../types/Event'; // Assume Event type is defined elsewhere
import EventCard from './EventCard'; // Reusable card for individual events
import { EventContext } from '../context/EventContext';

interface EventTableProps {
  allEvents: Event[];
  itemsPerPage?: number;
}

const EventTable: React.FC<EventTableProps> = ({ allEvents, itemsPerPage = 10 }) => {
  const { currentEvent, setCurrentEvent } = useContext(EventContext); // Assuming the event data is in context
  const [searchQuery, setSearchQuery] = useState('');
  const [sortedEvents, setSortedEvents] = useState<Event[]>(allEvents);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  useEffect(() => {
    // Sort and filter events whenever the search query or sort config changes
    let filteredEvents = allEvents.filter((event) =>
      [event.host_name, event.event_name, event.location, event.event_code]
        .some(field => field?.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (sortConfig) {
      filteredEvents.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof Event];
        const bValue = b[sortConfig.key as keyof Event];
        if (aValue && bValue && typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });
    }

    setSortedEvents(filteredEvents);
  }, [searchQuery, sortConfig, allEvents]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEvents = sortedEvents.slice(startIndex, startIndex + itemsPerPage);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const selectCurrentEvent = (event: Event) => {
    if (currentEvent) {
      if (currentEvent.event_id == event.event_id) {
        setCurrentEvent(null);
      } else {
        setCurrentEvent(event);
      }
    } else {
      setCurrentEvent(event);
    }
  };

  const handleSort = (key: keyof Event) => {
    setSortConfig((prevSortConfig) => {
      if (prevSortConfig && prevSortConfig.key === key) {
        // Toggle sort direction
        return {
          key,
          direction: prevSortConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' }; // Default to ascending
    });
  };

  const totalPages = Math.ceil(sortedEvents.length / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="event-table-container">
      <input
        type="text"
        placeholder="Search by host, name, location, or event code"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      
      <table className="event-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('host_name')}>Host Name</th>
            <th onClick={() => handleSort('event_name')}>Event Name</th>
            <th onClick={() => handleSort('location')}>Location</th>
            <th onClick={() => handleSort('event_type_name')}>Type</th>
            <th onClick={() => handleSort('event_code')}>Event Code</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentEvents.length > 0 ? (
            currentEvents.map((event) => (
              <tr key={event.event_id}>
                <td>{event.host_name}</td>
                <td>{event.event_name}</td>
                <td>{event.location}</td>
                <td>{event.event_type_name}</td>
                <td>{event.event_code}</td>
                <td><button onClick={() => selectCurrentEvent(event)} className='actionButton'>
    {event.event_id === currentEvent?.event_id ? (<>Selected <span style={{ color: 'green' }}>&#10003;</span></>) : (
              "Select Event")}
  </button>
</td></tr>
            ))
          ) : (
            <tr>
              <td colSpan={5}>No events found</td>
            </tr>
          )}
        </tbody>
      </table>
<br/>
      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span style={{margin: '10px'}}>Page {currentPage} of {totalPages}</span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default EventTable;
